// Gmail connection card — moved verbatim out of main-settings.jsx when the
// external connections were consolidated under Settings → Integrations.
// Per-USER (every agent connects their own Gmail), unlike the Admin-only
// API-key sub-tabs beside it.
import { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";
import gmail from "../../images/gmail.png";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import {
  getuserinformation,
  updateUserProfile,
} from "../../utils/firebasefunctions";

function GmailConnectionCard() {
  const [{ user }, dispatch] = useStateValue();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  useEffect(() => {
    // The Google Identity script loads from index.html; guard so a slow load
    // can't crash Settings (the button just retries via window.loginWithGoogle).
    if (!window.google?.accounts?.oauth2) {
      console.warn("Google Identity script not ready — Gmail connect deferred");
      return;
    }
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id:
        "627842934381-7bra6ha78jub716ceu9hq433chsh69dn.apps.googleusercontent.com",
      scope:
        "https://www.googleapis.com/auth/contacts https://www.googleapis.com/auth/gmail.send openid email profile",
      prompt: "consent",
      callback: (response) => {
        if (response.code) {
          sendtoken(response.code);
        }
      },
    });

    window.loginWithGoogle = () => {
      client.requestCode();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendtoken = async (token) => {
    setIsConnecting(true);
    try {
      const res = await fetch(
        "https://us-central1-team-edelstein.cloudfunctions.net/api/getgmailrefreshtoken",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            code: token,
            id: user.id,
          }),
        },
      );

      if (res.status === 200) {
        toast.success("Gmail Signed in Successfully!");
        const userInfo = await getuserinformation("users", user.id);
        dispatch({ type: actionType.SET_USER, payload: userInfo });
      } else {
        toast.error("Authentication Error");
      }
    } catch (error) {
      toast.error("Authentication Error");
      console.error("Error:", error);
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnect = async () => {
    setIsDisconnecting(true);
    const updatedProfile = {
      gmailtoken: {},
      gmailuserinfo: {},
      googleContactSync: {},
    };

    await updateUserProfile(user.id, updatedProfile).then(async () => {
      const userInfo = await getuserinformation("users", user.id);
      dispatch({ type: actionType.SET_USER, payload: userInfo });
      toast.success("Gmail Disconnected Successfully!");
      setIsDisconnecting(false);
    });
  };

  return (
    <div className="rounded-xl border border-gray-100 bg-white shadow-sm p-6">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
        Email Sending — Gmail
      </h2>

      <div
        className={`rounded-xl border ${user?.gmailtoken?.access_token ? "border-green-500 bg-green-50" : "border-gray-300"} mt-4 p-4 flex flex-row items-center justify-between gap-4`}
      >
        <div className="flex flex-row items-center">
          {user?.gmailuserinfo?.picture ? (
            <img
              src={user?.gmailuserinfo?.picture}
              alt="prop"
              className="w-[50px] h-[50px] object-contain rounded"
            />
          ) : (
            <img
              src={gmail}
              alt="prop"
              className="w-[50px] h-[50px] object-contain rounded"
            />
          )}

          {user?.gmailuserinfo?.email ? (
            <div className="ml-[20px]">
              <p class="text-green-900 text-[12px]">✓ CONNECTED</p>

              <p class="text-black  font-semibold text-[14px]">
                {user?.gmailuserinfo?.email}
              </p>
              <p class="text-gray-500   text-[12px]">
                {`Connected ${moment(user?.gmailuserinfo?.syncedAt).format("MMMM DD, YYYY")} · Auto-send and preview-and-send enabled`}
              </p>
            </div>
          ) : (
            <div className="ml-[20px] mr-[20px]">
              <p class="text-gray-500 mt-1 text-[0.95rem]">
                Connect your Gmail account so TES can send emails (vendor
                notifications, open house scheduling, seller recaps) directly
                from your address. Replies go to your inbox just like any
                other email. You can disconnect at any time.
              </p>
            </div>
          )}
        </div>
        <button
          className={`rounded-xl border border-gray-100 ${user?.gmailtoken?.access_token ? "hover:border-red-500 hover:bg-red-50 hover:text-red-500" : "hover:border-green-500 hover:bg-green-50 hover:text-green-500"}   bg-white shadow  p-2 text-[14px] text-gray-500`}
          onClick={() => {
            if (user?.gmailtoken?.access_token) {
              disconnect();
            } else if (window.loginWithGoogle) {
              window.loginWithGoogle();
            } else {
              toast.error("Google sign-in is still loading — try again in a moment");
            }
          }}
        >
          {isConnecting || isDisconnecting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {isConnecting ? "Connecting..." : "Disconnecting..."}
            </span>
          ) : user?.gmailtoken?.access_token ? (
            "Disconnect"
          ) : (
            "Connect"
          )}
        </button>
      </div>
    </div>
  );
}

export default GmailConnectionCard;
